import { useEffect } from 'react';

import { useI18n } from '@change-corgi/core-react/i18n';
import { useUtilityContext } from '@change-corgi/core-react/utilityContext';

import { usePsfSharePageData } from '#src/app/pages/psf/share/pageContext';
import type { ShareFeedItem } from '#src/app/pages/psf/share/pageContext';
import { useCurrentUser } from '#src/app/shared/hooks/session';
import { useTrackOnce } from '#src/app/shared/hooks/tracking';

import { readStoredGoal } from '../../../SetGoalStep/hooks/useSetGoalStep';

const MIN_SHARE_FEED_SHARES = 3;

export type UseReachGoalStepResult = {
	data: {
		goalValue: number;
		userName: string;
		shareFeed: {
			items: readonly ShareFeedItem[];
			showFeed: boolean;
		};
	};
};

export function useReachGoalStep(): UseReachGoalStepResult {
	const { translate } = useI18n();
	const { errorReporter } = useUtilityContext();
	const { data } = usePsfSharePageData();
	const petitionId = data.petition.id;
	const { shareFeed } = data;
	const user = useCurrentUser();
	const trackOnce = useTrackOnce();

	const goalValue = readStoredGoal(petitionId, errorReporter);
	const userName =
		user?.firstName ||
		user?.shortDisplayName ||
		user?.displayName ||
		translate('corgi.psf.share.sharing_goal.default_name');
	const showFeed = shareFeed.length >= MIN_SHARE_FEED_SHARES;

	useEffect(() => {
		if (!showFeed) return;
		void trackOnce('share_feed_view', {
			share_feed_item_count: shareFeed.length,
			share_feed_photo_count: shareFeed.filter((item) => !!item.userPhoto).length,
		});
	}, [trackOnce, shareFeed, showFeed]);

	return {
		data: {
			goalValue,
			userName,
			shareFeed: {
				items: shareFeed,
				showFeed,
			},
		},
	};
}
